import { useGenericSystemActionCatalog } from "#src/components/hooks/adapters/useWorkflowTemplates";
import {
  Accordion,
  EmptyState,
  KeyValueList,
  type KeyValuePairList,
  Skeleton,
} from "@validereinc/common-components";
import type { GenericSystemActionInput } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./WorkflowConfigurationGenericSystemActionPreview.module.scss";

const cx = classNames.bind(styles);
const { AccordionPanel } = Accordion;

type WorkflowConfigurationGenericSystemActionPreviewProps = {
  operation?: string;
  input?: GenericSystemActionInput;
  output?: Record<string, string>;
};

/** Read-only preview of a generic_system_action configuration. */
export const WorkflowConfigurationGenericSystemActionPreview = ({
  operation,
  input,
  output,
}: WorkflowConfigurationGenericSystemActionPreviewProps) => {
  const { data: catalogEntries, isLoading } = useGenericSystemActionCatalog();

  const entry = useMemo(
    () => catalogEntries?.find((e) => e.operation === operation),
    [catalogEntries, operation]
  );

  const overviewData: KeyValuePairList = useMemo(
    () => [
      {
        title: "Operation",
        value: entry?.display_name ?? operation ?? "-",
      },
      ...(entry?.configuration.execution_type === "api"
        ? [
            {
              title: "Method",
              value: entry.configuration.method,
            },
            {
              title: "Path",
              value: entry.configuration.path,
            },
          ]
        : []),
    ],
    [entry, operation]
  );

  const inputData: KeyValuePairList = useMemo(() => {
    const pairs: KeyValuePairList = [];
    if (input?.path) {
      for (const [key, value] of Object.entries(input.path)) {
        pairs.push({ title: `path.${key}`, value: String(value) });
      }
    }
    if (input?.query) {
      for (const [key, value] of Object.entries(input.query)) {
        pairs.push({ title: `query.${key}`, value: String(value) });
      }
    }
    return pairs;
  }, [input]);

  const bodyJson = useMemo(() => {
    if (!input?.body || Object.keys(input.body).length === 0) return null;
    return JSON.stringify(input.body, null, 2);
  }, [input]);

  const outputData: KeyValuePairList = useMemo(
    () =>
      output
        ? Object.entries(output).map(([key, value]) => ({
            title: key,
            value: String(value),
          }))
        : [],
    [output]
  );

  return (
    <Skeleton isLoading={isLoading}>
      <Accordion
        defaultActiveKeys={["gsa-overview", "gsa-input", "gsa-output"]}
      >
        <AccordionPanel
          dataKey="gsa-overview"
          title="Action Overview"
        >
          <KeyValueList
            data={overviewData}
            variant="shaded"
          />
        </AccordionPanel>

        <AccordionPanel
          dataKey="gsa-input"
          title="Input"
        >
          {inputData.length > 0 || bodyJson ? (
            <>
              {inputData.length > 0 && (
                <KeyValueList
                  data={inputData}
                  variant="shaded"
                />
              )}
              {bodyJson && (
                <div style={{ marginTop: inputData.length > 0 ? 8 : 0 }}>
                  <p className={cx("bodyLabel")}>Body</p>
                  <pre className={cx("bodyCodeBlock")}>
                    <code>{bodyJson}</code>
                  </pre>
                </div>
              )}
            </>
          ) : (
            <EmptyState title="No input parameters configured." />
          )}
        </AccordionPanel>

        <AccordionPanel
          dataKey="gsa-output"
          title="Output"
        >
          {outputData.length > 0 ? (
            <KeyValueList
              data={outputData}
              variant="shaded"
            />
          ) : (
            <EmptyState title="No output variables configured." />
          )}
        </AccordionPanel>
      </Accordion>
    </Skeleton>
  );
};
